import { noop } from 'lodash';
import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { useInView } from 'react-intersection-observer';

import { reportErrorToBugsnag } from '@air/utils-error';

import { WorkspaceAgentPendingPill } from '~/components/v2/components/workspace/workspace-agent-panel/components/shared/WorkspaceAgentPendingPill';
import { WorkspaceAgentPreviewCardCloseButton } from '~/components/v2/components/workspace/workspace-agent-panel/components/shared/WorkspaceAgentPreviewCardCloseButton';
import { WorkspaceAgentReferencePill } from '~/components/v2/components/workspace/workspace-agent-panel/components/shared/WorkspaceAgentReferencePill';
import { WorkspaceAgentShowcasePillLeadingVisual } from '~/components/v2/components/workspace/workspace-agent-panel/components/shared/WorkspaceAgentShowcasePillLeadingVisual';
import { WorkspaceAgentUnavailablePill } from '~/components/v2/components/workspace/workspace-agent-panel/components/shared/WorkspaceAgentUnavailablePill';
import { WorkspaceAgentShowcasePreviewCard } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/cards/WorkspaceAgentShowcasePreviewCard';
import { useOpenWorkspaceAgentShowcaseSettings } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useOpenWorkspaceAgentShowcaseSettings';
import { useWorkspaceAgentPreviewCardDisclosure } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useWorkspaceAgentPreviewCardDisclosure';
import { usePrivateWorkspaceAgentShowcaseMoniker } from '~/components/v2/components/workspace/workspace-agent-panel/queries/usePrivateWorkspaceAgentShowcaseMoniker';
import { isWorkspaceAgentShowcaseMonikerUnresolvableError } from '~/components/v2/components/workspace/workspace-agent-panel/utils/isWorkspaceAgentShowcaseMonikerUnresolvableError';
import { toWorkspaceAgentShareLinkDisplayUrl } from '~/components/v2/components/workspace/workspace-agent-panel/utils/toWorkspaceAgentShareLinkMonikerSubject';
import { useCopyTextToClipboard } from '~/hooks/useCopyTextToClipboard';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { getFullShareUrl } from '~/utils/shareUrl';

export type PrivateWorkspaceAgentPanelMessageShowcaseMonikerProps = {
  collectionId: string;
};

const reportedShowcaseCollectionIds = new Set<string>();

const reportShowcaseMonikerErrorOnce = (collectionId: string, error: unknown) => {
  if (reportedShowcaseCollectionIds.has(collectionId)) {
    return;
  }

  reportedShowcaseCollectionIds.add(collectionId);
  reportErrorToBugsnag({ error, context: 'Workspace agent showcase moniker could not resolve its collection' });
};

export const PrivateWorkspaceAgentPanelMessageShowcaseMoniker = memo(
  ({ collectionId }: PrivateWorkspaceAgentPanelMessageShowcaseMonikerProps) => {
    const triggerRef = useRef<HTMLButtonElement | null>(null);
    const { copyTextToClipboard } = useCopyTextToClipboard();
    const { openShowcaseSettings } = useOpenWorkspaceAgentShowcaseSettings();
    const { currentWorkspace } = useCurrentWorkspace();
    const workspaceId = currentWorkspace?.id;

    const focusTrigger = useCallback(() => {
      triggerRef.current?.focus();
    }, []);

    const { inView, ref } = useInView({ triggerOnce: true });

    const {
      data: subject,
      error,
      isLoading,
    } = usePrivateWorkspaceAgentShowcaseMoniker({ collectionId, workspaceId }, { enabled: inView });

    const shareUrl = useMemo(
      () => (subject?.shareShortId ? getFullShareUrl(subject.shareShortId) : null),
      [subject?.shareShortId],
    );
    const displayUrl = shareUrl ? toWorkspaceAgentShareLinkDisplayUrl(shareUrl) : null;

    const { closeCard, handleOpenChange, isCardOpen, isNavigatingAway, navigateFromCard } =
      useWorkspaceAgentPreviewCardDisclosure({
        focusNavigationOrigin: focusTrigger,
        hasSubject: !!subject,
        onCardOpen: noop,
      });

    useEffect(() => {
      if (error && !isWorkspaceAgentShowcaseMonikerUnresolvableError(error)) {
        reportShowcaseMonikerErrorOnce(collectionId, error);
      }
    }, [collectionId, error]);

    const handleCopyLink = useCallback(() => {
      if (!shareUrl) {
        return;
      }

      void copyTextToClipboard({
        successMessage: 'Share link copied to clipboard',
        text: shareUrl,
      });
    }, [copyTextToClipboard, shareUrl]);

    const handleOpenLink = useCallback(() => {
      if (!shareUrl) {
        return;
      }

      closeCard();
      window.open(shareUrl, '_blank', 'noopener,noreferrer');
    }, [closeCard, shareUrl]);

    const handleOpenSettings = useCallback(() => {
      navigateFromCard(() => {
        openShowcaseSettings(collectionId);
      });
    }, [collectionId, navigateFromCard, openShowcaseSettings]);

    const handleTriggerClick = shareUrl ? handleCopyLink : undefined;

    let chip = <WorkspaceAgentPendingPill label="Showcase" />;

    if (subject) {
      chip = (
        <WorkspaceAgentReferencePill
          card={
            <WorkspaceAgentShowcasePreviewCard
              closeButton={<WorkspaceAgentPreviewCardCloseButton onClose={closeCard} />}
              displayUrl={displayUrl}
              onCopyLink={handleCopyLink}
              onOpenLink={handleOpenLink}
              onOpenSettings={handleOpenSettings}
              thumbnailUrl={subject.thumbnailUrl}
              title={subject.title}
            />
          }
          isOpen={isCardOpen}
          leadingVisual={<WorkspaceAgentShowcasePillLeadingVisual />}
          onOpenChange={handleOpenChange}
          onTriggerClick={handleTriggerClick}
          shouldPreventCloseAutoFocus={isNavigatingAway}
          surface="history"
          testId="WORKSPACE_AGENT_MESSAGE_SHOWCASE_PILL"
          title={subject.title}
          triggerAriaLabel={shareUrl ? `Copy showcase link ${subject.title}` : `Showcase ${subject.title}`}
          triggerClassName="py-px pr-1.5 pl-px"
          triggerRef={triggerRef}
          triggerTestId="WORKSPACE_AGENT_MESSAGE_SHOWCASE_PILL_TRIGGER"
        />
      );
    } else if (error && !isLoading) {
      chip = <WorkspaceAgentUnavailablePill kind="showcase" referenceId={collectionId} />;
    }

    return (
      <span className="inline-flex max-w-full items-center align-middle leading-none" ref={ref}>
        {chip}
      </span>
    );
  },
);

PrivateWorkspaceAgentPanelMessageShowcaseMoniker.displayName = 'PrivateWorkspaceAgentPanelMessageShowcaseMoniker';
